import { getQuery, insertQuery, updateQuery, deleteQuery, uploadFileQuery } from "./UdasWrapper";
import { toast } from "react-toastify";
import { addErrorLog } from "Logger/logger";
import { compressFlow, uncompressRows, validateAndCleanFlow } from "helpers/common";
import { v4 as uuidv4 } from "uuid";
import { cacheUpdateSocketIoClient } from "../App";

// ***** USER FLOWS ***** //

export const fetchUserFlows = async (tenant_id) => {
   const data = JSON.stringify({
      query: `query UserFlows($tenant_id: String!) {
        userFlows(tenant_id: $tenant_id) {
          id
          tenant_id
          flow_id
          flow_name
          user_id
          user_name
          access_type
        }
      }`,
      variables: { tenant_id }
   });

   try {
      return (await getQuery(data))?.userFlows;
   } catch (error) {
      addErrorLog("query UserFlows", error, tenant_id);
      return null;
   }
};

// ***** FLOWS ***** //

export const fetchFlows = async (tenant_id) => {
   const data = JSON.stringify({
      query: `query Flows($tenant_id: String!) {
		flows(tenant_id: $tenant_id) {
		  id
		  name
		  description
		  steps
		  created_by
		  created_by_id
      created_date_time
		  platform_name
		  platform_type
		  notes
		  group_id
		  group_name
		  shared_status
		  shared_link
		  trashed
      updated_date_time
      flow_settings
      integrations
      logo
      documentation
      notify_for_moved_to_trash
      notify_for_delete
      connectors
      credentials
      current_version
      tenant_id
		}
	  }`,
      variables: { tenant_id }
   });
   try {
      const result = (await getQuery(data))?.flows;

      return uncompressRows(result);
   } catch (error) {
      addErrorLog("query Flows", error, tenant_id);
      return null;
   }
};

export const fetchFlowById = async (tenant_id, flow_id, version = "") => {
   const data = JSON.stringify({
      query: `query Flows($tenant_id: String!, $flow_id: String!, $version: String!) {
			getFlowByIdAndVersion(tenant_id: $tenant_id, flow_id: $flow_id, version: $version) {
				id
        tenant_id
				name
				description
				steps
				created_by
				created_by_id
        created_date_time
				platform_name
				platform_type
				notes
				group_id
				group_name
				shared_status
				shared_link
				trashed
        updated_date_time
        price
        publish_status
        flow_settings
        integrations
        connectors
        credentials
        current_version
			}
		}`,
      variables: { tenant_id, flow_id, version: version || "" }
   });
   try {
      const result = (await getQuery(data))?.getFlowByIdAndVersion;

      return uncompressRows(result);
   } catch (error) {
      console.log(error);
      return null;
   }
};

export const createFlow = async (tenant_id, input, showMessage, fromClone) => {
   console.log("CREATE FLOW (API call)");
   const flowId = uuidv4();

   input.id = flowId;
   input.steps = input?.steps && typeof input.steps === "string" ? JSON.parse(input.steps) : { context: {} };
   input.steps.context.flow_id = flowId;
   if (!input.connectors) input.connectors = [];
   if (!input.credentials) input.credentials = [];

   if (!input?.steps) console.info(`Validation for new flow succeeded`);
   else {
      const validationResult = validateAndCleanFlow(null, tenant_id, input);

      if (validationResult === 2) console.info(`Validation for new flow succeeded`);
      else if (validationResult === 1) {
         console.warn(`Validation for integrity of new flow ${input.name} revealed errors but flow will be saved`);
         toast.warn(`Validation for integrity of new flow ${input.name} revealed errors but flow will be saved`);
      } else {
         console.error(`Validation for integrity of new flow ${input.name} failed - flow is corrupted and will not be saved`);
         toast.error(`Validation for integrity of new flow failed - flow is corrupted and will not be saved`);
         return null;
      }
   }

   input = compressFlow(input);

   const data = JSON.stringify({
      query: `mutation CreateFlows($tenant_id: String!, $input: FlowsInput) {
            createFlows(tenant_id: $tenant_id, input: $input) {
              isSuccessful
              message
              resultObject
            }
          }`,
      variables: {
         tenant_id,
         input
      }
   });

   try {
      const result = (await insertQuery(data))?.createFlows;
      if (showMessage) {
         if (result?.isSuccessful)
            if (fromClone) toast.success(`Flow successfully cloned`);
            else toast.success(`New flow successfully saved`);
         else toast.error("Unable to save new flow - please try again");
      }
      if (result?.isSuccessful) return { id: flowId };
      else return null;
   } catch (error) {
      addErrorLog("mutation CreateFlows", error, tenant_id);
      return null;
   }
};

export const updateFlow = async (tenant_id, flowId, input, showMessage) => {
   console.log("UPDATE FLOW (API call)");

   if (cacheUpdateSocketIoClient?.connected) {
      cacheUpdateSocketIoClient.emit("flowUpdate", { tenantId: tenant_id, flowId, flowName: input.name });
   } else {
      console.error('Unable to request flow cache update in cxf-executor because the service is not running or not reachable');
      return null;
   }

   input.id = flowId;

   if (!input?.steps) console.info(`Validation for new flow succeeded`);
   else {
      const validationResult = validateAndCleanFlow(flowId, tenant_id, input);

      if (validationResult === 2) console.info(`Validation for new flow succeeded`);
      else if (validationResult === 1) {
         console.warn(`Validation for integrity of new flow ${input.name} revealed errors but flow will be saved`);
         toast.warn(`Validation for integrity of new flow ${input.name} revealed errors but flow will be saved`);
      } else {
         console.error(`Validation for integrity of new flow ${input.name} failed - flow is corrupted and will not be saved`);
         toast.error(`Validation for integrity of new flow failed - flow is corrupted and will not be saved`);
         return null;
      }
   }

   if (!input.connectors) input.connectors = [];
   if (!input.credentials) input.credentials = [];

   input = compressFlow(input);

   const data = JSON.stringify({
      query: `mutation UpdateFlows($tenant_id: String!, $input: FlowsInput) {
			updateFlows(tenant_id: $tenant_id, input: $input) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         input
      }
   });

   try {
      const result = (await updateQuery(data))?.updateFlows;

      if (showMessage)
         if (result?.isSuccessful) toast.success(`Flow successfully updated`);
         else toast.error("Unable to update flow - please try again");

      return result?.isSuccessful;
   } catch (error) {
      addErrorLog("mutation UpdateFlows", error, tenant_id);
      return null;
   }
};

export const deleteFlowPermanently = async (tenant_id, flowId) => {
   const data = JSON.stringify({
      query: `mutation DeleteFlowPermanently($tenant_id: String!, $flowId: String!) {
			deleteFlowPermanently(tenant_id: $tenant_id, flow_id: $flowId) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         flowId
      }
   });

   try {
      return (await updateQuery(data))?.deleteFlowPermanently;
   } catch (error) {
      addErrorLog("mutation DeleteFlowPermanently", error, tenant_id);
      return null;
   }
};

export const updateFlowThumbnail = async (tenant_id, flowId, file) => {
   try {
      let filename;

      if (file.name) {
         // Extract file extension
         const fileExtension = "png";

         // Create the filename with the modified string and the file extension
         filename = flowId + "." + fileExtension?.toLowerCase();
      }

      let modifiedFile = new File([file], filename, { type: file.type });
      let toUploadData;

      toUploadData = new FormData();

      toUploadData.append(
         "operations",
         JSON.stringify({
            query: `mutation UpdateFlowThumbnail($file: Upload!, $flowId: ID!, $tenant_id: String!) {
        updateFlowThumbnail(file: $file, flow_id: $flowId, tenant_id: $tenant_id) {
          isSuccessful
          message
          resultObject
        }
      }`,
            variables: {
               file: null,
               flowId: flowId,
               tenant_id: tenant_id
            }
         })
      );
      toUploadData.append("map", JSON.stringify({ 0: ["variables.file"] }));
      toUploadData.append("0", modifiedFile);

      await uploadFileQuery(toUploadData);
   } catch (error) {
      addErrorLog("mutation UpdateFlowThumbnail", error, tenant_id);
      return null;
   }
};

export const shareFlow = async (tenant_id, input) => {
   let data = JSON.stringify({
      query: `mutation CreateUserFlowsMultiple($tenant_id: String!, $input: UserFlowsMultiInput) {
      createUserFlowsMultiple(tenant_id: $tenant_id, input: $input) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id,
         input
      }
   });

   try {
      return (await insertQuery(data))?.createUserFlowsMultiple;
   } catch (error) {
      addErrorLog("mutation CreateUserFlowsMultiple", error, tenant_id);
      return null;
   }
};

export const updateShareFlow = async (tenant_id, input) => {
   let data = JSON.stringify({
      query: `mutation UpdateUserFlowsMultiple($tenant_id: String!, $input: UpdateUserFlowsMultiInputs) {
      updateUserFlowsMultiple(tenant_id: $tenant_id, input: $input) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id,
         input
      }
   });

   try {
      return (await insertQuery(data))?.updateUserFlowsMultiple?.resultObject;
   } catch (error) {
      addErrorLog("mutation UpdateUserFlowsMultiple", error, tenant_id);
      return null;
   }
};

export const unshareFlow = async (tenant_id, filterUserFlowsId) => {
   let data = JSON.stringify({
      query: `mutation DeleteMultipleUserFlows($tenant_id: String!, $userFlowIds: [ID]) {
      deleteMultipleUserFlows(tenant_id: $tenant_id, user_flow_ids: $userFlowIds) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id,
         userFlowIds: filterUserFlowsId
      }
   });

   try {
      return (await deleteQuery(data))?.deleteMultipleUserFlows?.resultObject;
   } catch (error) {
      addErrorLog("mutation DeleteMultipleUserFlows", error, tenant_id);
      return null;
   }
};

export const updateUserFlows = async (tenant_id, input) => {
   let data = JSON.stringify({
      query: `mutation UpdateUserFlows($tenant_id: String!, $input: UserFlowsInput) {
      updateUserFlows(tenant_id: $tenant_id, input: $input) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id,
         input
      }
   });

   try {
      return (await updateQuery(data))?.updateUserFlows?.resultObject;
   } catch (error) {
      addErrorLog("mutation UpdateUserFlows", error, tenant_id);
      return null;
   }
};

// ***** USER GROUPS ***** //

export const fetchUserComponentGroups = async (tenant_id) => {
   let data = JSON.stringify({
      query: `query FetchUserComponentGroups($tenant_id: String!) {
			userComponentGroups(tenant_id: $tenant_id) {
					id
					name
					user_id
					is_hidden
					created_date_time
          component_groups {
            is_hidden
            component_id
          }
        }
      }`,
      variables: { tenant_id }
   });
   try {
      return (await getQuery(data))?.userComponentGroups;
   } catch (err) {
      addErrorLog("query FetchUserComponentGroups", err, tenant_id);
      return null;
   }
};

export const createUserComponentGroup = async (tenant_id, input) => {
   const data = JSON.stringify({
      query: `mutation CreateUserComponentGroups($tenant_id: String!, $input: UserComponentGroupsInput) {
			createUserComponentGroups(tenant_id: $tenant_id, input: $input) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         input
      }
   });

   try {
      return (await insertQuery(data))?.createUserComponentGroups?.resultObject;
   } catch (error) {
      addErrorLog("mutation CreateUserComponentGroups", error, tenant_id);
      return null;
   }
};

export const updateUserComponentGroup = async (tenant_id, groupId, input) => {
   const data = JSON.stringify({
      query: `mutation UpdateUserComponentGroups($tenant_id: String!, $groupId: String!, $input: UserComponentGroupsInput) {
			updateUserComponentGroups(tenant_id: $tenant_id, id: $groupId input: $input) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         groupId,
         input
      }
   });

   try {
      await updateQuery(data);
   } catch (error) {
      addErrorLog("mutation UpdateUserComponentGroups", error, tenant_id);
   }
};

export const deleteUserComponentGroup = async (tenant_id, groupId) => {
   const data = JSON.stringify({
      query: `mutation DeleteUserComponentGroups($tenant_id: String!, $groupId: String!) {
			deleteUserComponentGroups(tenant_id: $tenant_id, id: $groupId) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         groupId
      }
   });

   try {
      await deleteQuery(data);
   } catch (error) {
      addErrorLog("mutation DeleteUserComponentGroups", error, tenant_id);
   }
};

// ***** COMPONENT GROUP COMPONENTS ***** //

export const fetchComponentGroupComponents = async (tenant_id) => {
   let data = JSON.stringify({
      query: `query FetchComponentGroupComponents($tenant_id: String!) {
			componentGroupComponents(tenant_id: $tenant_id) {
					id
					user_component_groups_id
					component_id
					is_hidden
        }
      }`,
      variables: { tenant_id }
   });
   try {
      return (await getQuery(data))?.componentGroupComponents;
   } catch (err) {
      addErrorLog("query FetchComponentGroupComponents", err, tenant_id);
      return null;
   }
};

// ***** COMPONENTS ***** //

export const registerComponent = async (tenant_id, input) => {
   const data = JSON.stringify({
      query: `mutation CreateCxfComponents($tenant_id: String!, $input: CxfComponentsInput) {
			createCxfComponents(tenant_id: $tenant_id, input: $input) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         input
      }
   });

   try {
      const result = await insertQuery(data);

      if (result.error) throw new Error(result.error);

      return result?.createCxfComponents;
   } catch (error) {
      addErrorLog("mutation CreateCxfComponents", error, tenant_id);
      toast.error("Failed to upload the connector.");
      return null;
   }
};

export const updateComponent = async (tenant_id, id, input) => {
   const data = JSON.stringify({
      query: `mutation UpdateCxfComponents($tenant_id: String!, $id: String!, $input: CxfComponentsInput) {
			updateCxfComponents(tenant_id: $tenant_id, id: $id, input: $input) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         id,
         input
      }
   });

   try {
      const response = await updateQuery(data);
      const result = response?.updateCxfComponents;

      if (!result) return null;

      return {
         ...input,
         ...(result?.resultObject || {}),
         id,
         tenant_id,
         isSuccessful: result?.isSuccessful,
         message: result?.message
      };
   } catch (error) {
      addErrorLog("mutation UpdateCxfComponents", error, tenant_id);
      toast.error("Failed to upload the component.");
      return null;
   }
};

export const fetchComponents = async (tenant_id) => {
   const data = JSON.stringify({
      query: `query CxfComponents($tenant_id: String!) {
      cxfComponents(tenant_id: $tenant_id) {
        id
        created_date_time
        name
        description
        config_files
		    parent_component_id
		    user_name
        version
        status
        scope
        tenant_id
        price
        tags
        logo
        component_type
        industry
        support
        browser_compatibility
        developer
        developer_logo
        updated_date_time
        documentation,
        schema
      }
    }`,
      variables: { tenant_id }
   });

   try {
      return (await getQuery(data))?.cxfComponents;
   } catch (err) {
      addErrorLog("query CxfComponents", err, tenant_id);
      return null;
   }
};

export const deleteComponent = async (tenant_id, componentId) => {
   const data = JSON.stringify({
      query: `mutation DeleteCxfComponents($tenant_id: String!, $componentId: String!) {
			deleteCxfComponents(tenant_id: $tenant_id, id: $componentId) {
				isSuccessful
				message
				resultObject
			}
		}`,
      variables: {
         tenant_id,
         componentId
      }
   });

   try {
      const response = (await deleteQuery(data))?.deleteCxfComponents;
      return response;
   } catch (error) {
      addErrorLog("mutation DeleteCxfComponents", error, tenant_id);
      return null;
   }
};

// ***** SUBSCRIPTION USAGE ***** //
export const checkTenantSubscription = async (tenant_id) => {
   let data = JSON.stringify({
      query: `query CheckTenantSubscription($tenant_id: String!) {
      checkTenantSubscription(tenant_id: $tenant_id) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id: tenant_id
      }
   });
   try {
      return (await getQuery(data))?.checkTenantSubscription?.resultObject;
   } catch (error) {
      addErrorLog("query CheckTenantSubscription", error, tenant_id);
      return null;
   }
};

// ***** SCHEDULER ***** //

export const fetchSchedule = async (tenant_id) => {
   const data = JSON.stringify({
      query: `query GetFlowSchedule($tenant_id: String!) {
      getFlowSchedule(tenant_id: $tenant_id) {
        end_date
        id
        name
        start_date
        description
        repeat_type,
        status,
        repetition
        created_by_id
        updated_date_time,
        created_date_time,
        documentation,
        tenant_id,
        notes
      }
    }`,
      variables: { tenant_id }
   });
   try {
      const result = (await getQuery(data))?.getFlowSchedule;

      return uncompressRows(result);
   } catch (error) {
      addErrorLog("query GetFlowSchedule", error, tenant_id);
      return null;
   }
};

export const fetchScheduleById = async (tenant_id, id) => {
   const data = JSON.stringify({
      query: `query GetFlowScheduleById($tenant_id: String!, $id: String!) {
      getFlowScheduleById(tenant_id: $tenant_id, id: $id) {
        created_date_time
        description
        end_date
        id
        name
        repeat_type
        start_date
        tenant_id,
        status,
        repetition,
        updated_date_time,
        documentation
      }
    }`,
      variables: { tenant_id, id }
   });
   try {
      const result = (await getQuery(data))?.getFlowScheduleById;

      return uncompressRows(result);
   } catch (error) {
      addErrorLog("query GetFlowScheduleById", error, tenant_id);
      return null;
   }
};

export const fetchScheduleByTenantAndFlowId = async (tenant_id, flow_id) => {
   const data = JSON.stringify({
      query: `query GetFlowScheduleByTenantAndFlowId($tenant_id: String!, $flow_id: String!) {
      getFlowScheduleByTenantAndFlowId(tenant_id: $tenant_id, flow_id: $flow_id) {
        id
        name
      }
    }`,
      variables: { tenant_id, flow_id }
   });
   try {
      return (await getQuery(data))?.getFlowScheduleByTenantAndFlowId;
   } catch (error) {
      addErrorLog("query GetFlowScheduleByTenantAndFlowId", error, tenant_id);
      return null;
   }
};

export const createSchedule = async (tenant_id, input) => {
   const data = JSON.stringify({
      query: `mutation CreateFlowSchedule($tenant_id: String!, $input: FlowSchedulesInput) {
      createFlowSchedule(tenant_id: $tenant_id, input: $input) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         input,
         tenant_id
      }
   });

   try {
      const result = await insertQuery(data);
      if (result?.createFlowSchedule?.isSuccessful) {
         toast.success(`Schedule successfully created.`);
         return Object.assign(result.createFlowSchedule.resultObject, input);
      }

      toast.error(result?.createFlowSchedule?.message);
      return null;
   } catch (error) {
      addErrorLog("mutation CreateFlowSchedule", error, tenant_id);
      toast.error("Failed to save the schedule");
      return null;
   }
};

export const updateSchedule = async (tenant_id, id, input) => {
   const data = JSON.stringify({
      query: `mutation UpdateFlowSchedule($tenant_id: String!, $id: String!, $input: FlowSchedulesInput) {
      updateFlowSchedule(tenant_id: $tenant_id, id: $id, input: $input) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id,
         id,
         input
      }
   });

   try {
      const result = await updateQuery(data);
      toast.success(`Schedule successfully updated.`);
      return result?.updateFlowSchedule?.resultObject;
   } catch (error) {
      toast.error("Failed to save the schedule");
      addErrorLog("mutation UpdateFlowSchedule", error, tenant_id);
   }
};

export const updateScheduleStatus = async (tenant_id, id, status) => {
   const data = JSON.stringify({
      query: `mutation UpdateFlowScheduleStatus($tenant_id: String!, $id: String!, $status: String!) {
      updateFlowScheduleStatus(tenant_id: $tenant_id, id: $id,  status: $status) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id,
         id,
         status
      }
   });

   try {
      const result = await updateQuery(data);
      toast.success(`Schedule status successfully updated.`);
      return result?.updateFlowScheduleStatus?.resultObject;
   } catch (error) {
      toast.error("Failed to update schedule status");
      addErrorLog("mutation UpdateFlowScheduleStatus", error, tenant_id);
   }
};

export const deleteSchedule = async (tenant_id, id) => {
   const data = JSON.stringify({
      query: `mutation DeleteFlowSchedule($tenant_id: String!, $id: String!) {
      deleteFlowSchedule(tenant_id: $tenant_id, id: $id) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
         tenant_id,
         id
      }
   });

   try {
      const response = (await deleteQuery(data))?.deleteFlowSchedule;
      return response;
   } catch (error) {
      addErrorLog("mutation DeleteFlowSchedule", error, tenant_id);
      return null;
   }
};

export const updateScheduleDocumentation = async (tenant_id, id, documentation) => {
   try {
      const data = JSON.stringify({
         query: `mutation UpdateScheduleDocumentation($tenantId: String!, $updateScheduleDocumentationId: String!, $documentation: String) {
  updateScheduleDocumentation(tenant_id: $tenantId, id: $updateScheduleDocumentationId, documentation: $documentation) {
    isSuccessful
    message
    resultObject
  }
}`,
         variables: {
            tenantId: tenant_id,
            updateScheduleDocumentationId: id,
            documentation: documentation
         }
      });
      return (await updateQuery(data))?.updateScheduleDocumentation?.resultObject;
   } catch (error) {
      addErrorLog("mutation UpdateScheduleDocumentation", error, tenant_id);
   }
};

export const fetchSharedSchedules = async (tenant_id) => {
   try {
      const data = JSON.stringify({
         query: `query GetSharedSchedules($input: GetSharedSchedulesInput) {
  getSharedSchedules(input: $input) {
    isSuccessful
    message
    resultObject
  }
}`,
         variables: {
            input: {
               tenant_id: tenant_id
            }
         }
      });

      return (await getQuery(data))?.getSharedSchedules?.resultObject;
   } catch (error) {
      addErrorLog("query FetchSharedSchedules", error);
   }
};

export const insertSharedSchedules = async (tenant_id, input) => {
   try {
      const data = JSON.stringify({
         query: `mutation InsertSharedSchedules($tenant_id: String!, $input: [SharedScheduleInput]) {
  insertSharedSchedules(tenant_id: $tenant_id, input: $input) {
    isSuccessful
    message
    resultObject
  }
}`,
         variables: { tenant_id, input }
      });

      return (await insertQuery(data))?.insertSharedSchedules;
   } catch (error) {
      addErrorLog("mutation InsertSharedSchedule", error);
   }
};

export const deleteSharedSchedules = async (tenant_id, input) => {
   try {
      const data = JSON.stringify({
         query: `mutation DeleteSharedSchedules($tenant_id: String!, $input: [ID]) {
  deleteSharedSchedules(tenant_id:$tenant_id, input: $input) {
    isSuccessful
    message
    resultObject
  }
}`,
         variables: { tenant_id, input }
      });

      return (await deleteQuery(data))?.deleteSharedSchedules?.resultObject;
   } catch (error) {
      addErrorLog("mutation DeleteSharedSchedules", error);
   }
};
//  ***** FLOW SCHEDULE BY FLOW ***** //
export const insertScheduleByFlow = async (tenant_id, flow_id, new_schedules, removed_schedules) => {
   const data = JSON.stringify({
      query: `mutation InsertScheduleByFlow($tenantId: String!, $flowId: ID!, $newSchedules: [ID], $removedSchedules: [ID]) {
  insertScheduleByFlow(tenant_id: $tenantId, flow_id: $flowId, new_schedules: $newSchedules, removed_schedules: $removedSchedules) {
    isSuccessful
    message
    resultObject
  }
}
`,
      variables: {
         tenantId: tenant_id,
         flowId: flow_id,
         newSchedules: new_schedules,
         removedSchedules: removed_schedules
      }
   });

   try {
      return (await insertQuery(data))?.insertScheduleByFlow;
   } catch (error) {
      addErrorLog("mutation InsertScheduleByFlow", error, tenant_id);
   }
};

export const deleteScheduleByFlowByFlowId = async (tenant_id, flow_id) => {
   const data = JSON.stringify({
      query: `mutation DeleteScheduleByFlowByFlowId($flowId: ID!, $tenantId: String!) {
  deleteScheduleByFlowByFlowId(flow_id: $flowId, tenant_id: $tenantId) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: {
         flowId: flow_id,
         tenantId: tenant_id
      }
   });
   try {
      return (await deleteQuery(data))?.deleteScheduleByFlowByFlowId;
   } catch (error) {
      addErrorLog("mutation DeleteScheduleByFlowByFlowId", error, tenant_id);
   }
};

export const getScheduleByFlowByScheduleId = async (schedule_id) => {
   const data = JSON.stringify({
      query: `query GetScheduleByFlowByScheduleId($scheduleId: ID!) {
  getScheduleByFlowByScheduleId(schedule_id: $scheduleId) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: { scheduleId: schedule_id }
   });

   try {
      return (await getQuery(data))?.getScheduleByFlowByScheduleId;
   } catch (error) {
      addErrorLog("query GetScheduleByFlowByScheduleId", error);
   }
};

export const getScheduleByFlowId = async (tenant_id, flow_id) => {
   const data = JSON.stringify({
      query: `query GetScheduleByFlowId($tenantId: String!, $flowId: ID!) {
  getScheduleByFlowId(tenant_id: $tenantId, flow_id: $flowId) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: { tenantId: tenant_id, flowId: flow_id }
   });
   try {
      return (await getQuery(data))?.getScheduleByFlowId;
   } catch (error) {
      addErrorLog("query GetScheduleByFlowId", error, tenant_id);
   }
};

// ***** EXECUTION LOGS ***** //

export const getExecutionLogs = async (tenant_id) => {
   const data = JSON.stringify({
      query: `query ExecutionLogs($tenant_id: String!) {
      executionLogs(tenant_id: $tenant_id) {
        ended_date_time
        error_messages
        execution_plan_id
        execution_plan_name
        execution_batch_datetime
        flow_name
        flow_id
        action
        id
        started_date_time
        status
        step
        tenant_id
      }
    }`,
      variables: {
         tenant_id
      }
   });

   try {
      return (await getQuery(data))?.executionLogs;
   } catch (error) {
      addErrorLog("query ExecutionLogs", error, tenant_id);
   }
};

export const getExecutionLogsFlowSchedule = async (tenant_id, schedule_id) => {
   const data = JSON.stringify({
      query: `query GetExecutionLogsByScheduleId($tenant_id: String!, $schedule_id: String!) {
      getExecutionLogsByScheduleId(tenant_id: $tenant_id, schedule_id: $schedule_id) {
        id
        tenant_id
        execution_plan_id
        execution_plan_name
        flow_id
        flow_name
        started_date_time
        ended_date_time
        step
        status
        error_messages
        execution_batch_datetime
        action
      }
    }`,
      variables: {
         tenant_id,
         schedule_id
      }
   });

   try {
      return (await getQuery(data))?.getExecutionLogsFlowSchedules;
   } catch (error) {
      addErrorLog("query GetExecutionLogsByScheduleId", error, tenant_id);
   }
};

/***** USERS *****/

export const getUserFlows = async (tenant_id) => {
   let data = JSON.stringify({
      query: `query UserFlows($tenant_id: String!) {
    userFlows(tenant_id: $tenant_id) {
      id
      tenant_id
      flow_id
      flow_name
      user_id
      user_name
      access_type
    }
  }`,
      variables: { tenant_id }
   });
   try {
      return (await getQuery(data))?.userFlows;
   } catch (error) {
      addErrorLog("query UserFlows", error, tenant_id);
      return null;
   }
};

/***** JSON *****/

export const storeOneJson = async (key, path = "", value, merge = false, ttl = 0) => {
   const data = JSON.stringify({
      query: `mutation StoreOneJson($key: String!, $path: String!, $value: JSON!, $merge: Boolean, $ttl: Int) {
  storeOneJson(key: $key, path: $path, value: $value, merge: $merge, ttl: $ttl) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: {
         key,
         path,
         value,
         merge,
         ttl
      }
   });
   try {
      return (await insertQuery(data))?.storeOneJson;
   } catch (error) {
      addErrorLog("mutation StoreOneJson", error);
      return null;
   }
};

export const retrieveJson = async (key, path = "") => {
   const data = JSON.stringify({
      query: `query RetrieveJson($key: String!, $path: String!) {
  retrieveJson(key: $key, path: $path) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: {
         key,
         path
      }
   });
   try {
      return (await getQuery(data))?.retrieveJson;
   } catch (error) {
      addErrorLog("query RetrieveJson", error);
   }
};

export const getSharedFlowConfigurations = async (tenant_id, configuration_keys) => {
   const data = JSON.stringify({
      query: `query GetSharedFlowConfigurations($tenantId: String!, $configurationKeys: [String!]) {
  getSharedFlowConfigurations(tenant_id: $tenantId, configuration_keys: $configurationKeys) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: { tenantId: tenant_id, configurationKeys: configuration_keys }
   });

   try {
      return (await getQuery(data))?.getSharedFlowConfigurations;
   } catch (error) {
      addErrorLog("query GetSharedFlowConfigurations", error, tenant_id);
      return null;
   }
};

export const getFlowSessions = async (tenant_id) => {
   const data = JSON.stringify({
      query: `query GetFlowSessions($tenantId: String!) {
  getFlowSessions(tenant_id: $tenantId) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: { tenantId: tenant_id }
   });

   try {
      return (await getQuery(data))?.getFlowSessions;
   } catch (error) {
      addErrorLog("query GetFlowSessions", error, tenant_id);
      return null;
   }
};

export const getFlowSession = async (tenant_id, session_id) => {
   const data = JSON.stringify({
      query: `query GetFlowSession($tenantId: String!, $sessionId: String!) {
  getFlowSession(tenant_id: $tenantId, session_id: $sessionId) {
    isSuccessful
    message
    resultObject
  }
}`,
      variables: { tenantId: tenant_id, sessionId: session_id }
   });

   try {
      return (await getQuery(data))?.getFlowSession;
   } catch (error) {
      addErrorLog("query GetFlowSession", error, tenant_id);
      return null;
   }
};

const mutateSharedFlowConfiguration = async (operation, variables) => {
   const data = JSON.stringify({ query: operation, variables });
   return await insertQuery(data);
};

export const createSharedFlowConfiguration = async (tenant_id, user_id, input) => {
   try {
      const response = await mutateSharedFlowConfiguration(
         `mutation CreateSharedFlowConfiguration($tenantId: String!, $userId: String!, $input: SharedFlowConfigurationInput!) {
  createSharedFlowConfiguration(tenant_id: $tenantId, user_id: $userId, input: $input) {
    isSuccessful
    message
    resultObject
  }
}`,
         { tenantId: tenant_id, userId: user_id, input }
      );
      return response?.createSharedFlowConfiguration;
   } catch (error) {
      addErrorLog("mutation CreateSharedFlowConfiguration", error, tenant_id);
      return null;
   }
};

export const updateSharedFlowConfiguration = async (tenant_id, user_id, configuration_key, input) => {
   try {
      const response = await mutateSharedFlowConfiguration(
         `mutation UpdateSharedFlowConfiguration($tenantId: String!, $userId: String!, $configurationKey: String!, $input: SharedFlowConfigurationInput!) {
  updateSharedFlowConfiguration(tenant_id: $tenantId, user_id: $userId, configuration_key: $configurationKey, input: $input) {
    isSuccessful
    message
    resultObject
  }
}`,
         { tenantId: tenant_id, userId: user_id, configurationKey: configuration_key, input }
      );
      return response?.updateSharedFlowConfiguration;
   } catch (error) {
      addErrorLog("mutation UpdateSharedFlowConfiguration", error, tenant_id);
      return null;
   }
};

export const deleteSharedFlowConfiguration = async (tenant_id, user_id, configuration_key) => {
   try {
      const response = await mutateSharedFlowConfiguration(
         `mutation DeleteSharedFlowConfiguration($tenantId: String!, $userId: String!, $configurationKey: String!) {
  deleteSharedFlowConfiguration(tenant_id: $tenantId, user_id: $userId, configuration_key: $configurationKey) {
    isSuccessful
    message
    resultObject
  }
}`,
         { tenantId: tenant_id, userId: user_id, configurationKey: configuration_key }
      );
      return response?.deleteSharedFlowConfiguration;
   } catch (error) {
      addErrorLog("mutation DeleteSharedFlowConfiguration", error, tenant_id);
      return null;
   }
};
